import { useCallback, useEffect, useMemo, useState } from "react";
import AccountsApi from "../../../api/AccountsApi";
import { useRecoilState } from "recoil";
import { memberState } from "../../../state/atoms";
import {
  AddOnDto,
  AddOnDtoTypeEnum,
  SubStatusDto,
  SubStatusDtoPlanCycleEnum,
  SubStatusDtoPlanTypeEnum,
  SubStatusDtoStatusEnum
} from "../../../generated/account-client";
import "./SubscriptionPage.scss";
import DateTimeStamp from "../misc/DateTimeStamp";
import { handleServerError } from "../../../util/ErrorHandler";
import { getAddonsRecurringCharge } from "../../../service/SubscriptionService";
import Price from "../misc/Price";
import { useNavigate } from "react-router-dom";
import { DashboardDtoStripePlanTypeEnum, MemberDtoPlanEnum } from "../../../generated/dashboard-client";
import { Button, Skeleton } from "antd";
import { env } from "../../../env/env";
import CloverLogo from "../../icons/CloverLogo";
import ProStarFilledIcon from "../../icons/ProStarFilledIcon";
import LocationIconRoundCircle from "../../icons/LocationIconRoundCircle";
import DSAApi from "../../../api/DSAApi";
import DesignPlanIcon from "../../icons/DesignPlanIcon";
import { SubscriptionFreePageV2 } from "./SubscriptionFreePageV2";
import LoseAccessWarningModal from "../modal/LoseAccessWarningModal";

const NO_ADDON = '';
export function SubscriptionPage() {
  const navigate = useNavigate();

  const [member, setMember] = useRecoilState(memberState);
  const [loading, setLoading] = useState(true);
  const [pauseLoading, setPauseLoading] = useState(false);
  const [addons, setAddons] = useState([] as AddOnDto[]);
  const [cycle, setCycle] = useState(SubStatusDtoPlanCycleEnum.No as SubStatusDtoPlanCycleEnum);
  const [planType, setPlanType] = useState<SubStatusDtoPlanTypeEnum>();
  const [status, setStatus] = useState(SubStatusDtoStatusEnum.Inactive as SubStatusDtoStatusEnum);
  const [planEndsAt, setPlanEndsAt] = useState("");
  const [addonsEndAt] = useState("");
  const [nextPlanBillingAt, setNextPlanBillingAt] = useState("");
  const [pauseStartAt, setPauseStartAt] = useState("");
  const [pauseEndAt, setPauseEndAt] = useState("");
  const [, setEmail] = useState("");
  const [charge, setCharge] = useState(0);
  const [deactivatedDevices, setDeactivatedDevices] = useState(0);
  const [pendingUpgrade, setPendingUpgrade] = useState(false);
  const [trial, setTrial] = useState(false);
  const [hasBillingDateNextMonth, setHasBillingDateNextMonth] = useState(false);
  const [pauseButton, setPauseButton] = useState(false);
  const [switchToAnnualButton, setSwitchToAnnualButton] = useState(false);
  const [switchToMonthlyButton, setSwitchToMonthlyButton] = useState(false);
  const [upgradeToProButton, setUpgradeToProButton] = useState(false);
  const [downgradeToDesignButton, setDowngradeToDesignButton] = useState(false);
  const [downgradeToDesignButtonDisabled, setDowngradeToDesignButtonDisabled] = useState(false);
  const [stopPauseButton, setStopPauseButton] = useState(false);
  const [cancelButton, setCancelButton] = useState(false);
  const [stopCancelButton, setStopCancelButton] = useState(false);
  const [isShamrock, setIsShamrock] = useState(false);
  const [userCount, setUserCount] = useState(0);
  const [showLoseAccessWarningModal, setShowLoseAccessWarningModal] = useState(false);

  const setSubscriptionData = useCallback((data: SubStatusDto) => {
    const {
      addons,
      planCycle,
      status,
      planEndDate,
      nextPlanBillingDate,
      email,
      planUpgradeInProgress,
      planTotal,
      pauseStartDate,
      pauseEndDate,
      trialEnd,
      planType
    } = data;

    setPlanType(planType);
    setAddons(addons || []);
    setCycle(planCycle || SubStatusDtoPlanCycleEnum.No);
    setStatus(status || SubStatusDtoStatusEnum.Inactive);
    setPlanEndsAt(planEndDate || "");

    if (member.partner === "shamrock") {
      setIsShamrock(true);
    } else {
      setIsShamrock(false);
    }

    setNextPlanBillingAt(nextPlanBillingDate || "");
    setPauseStartAt(pauseStartDate || "")
    setPauseEndAt(pauseEndDate || "")
    setPendingUpgrade(!!planUpgradeInProgress);
    setCharge(planTotal || 0);
    setTrial(!!trialEnd && (new Date() < new Date(trialEnd)));
    setUpgradeToProButton(planType === SubStatusDtoPlanTypeEnum.Design);
    if (email) {
      setEmail(email);
    }
  }, [member.partner]);

  useEffect(() => {
    if (planEndsAt) {
      setHasBillingDateNextMonth(
        new Date(planEndsAt).getMonth() !== new Date().getMonth()
      );
    } else {
      setHasBillingDateNextMonth(false);
    }
  }, [planEndsAt]);

  useEffect(() => {
    setPauseButton(
      cycle === SubStatusDtoPlanCycleEnum.Monthly &&
      status === SubStatusDtoStatusEnum.Active &&
      !pendingUpgrade && !trial
    );
  }, [cycle, status, trial, pendingUpgrade]);

  useEffect(() => {
    setStopPauseButton(
      cycle === SubStatusDtoPlanCycleEnum.Monthly &&
      (status === SubStatusDtoStatusEnum.Paused ||
        status === SubStatusDtoStatusEnum.WillPause)
    );
  }, [status, cycle]);

  useEffect(() => {
    setCancelButton(
      (cycle === SubStatusDtoPlanCycleEnum.Yearly &&
        ((status !== SubStatusDtoStatusEnum.Inactive && !pendingUpgrade) ||
          (status === SubStatusDtoStatusEnum.Active && hasBillingDateNextMonth))) ||
      trial
    );
  }, [hasBillingDateNextMonth, pendingUpgrade, status, cycle, trial]);

  useEffect(() => {
    setStopCancelButton(status === SubStatusDtoStatusEnum.Inactive);
  }, [status, cycle]);

  const fetchSubscription = useCallback(() => {
    if (!member.id) return;
    if (member.clover) {
      setLoading(false);
      return;
    }

    AccountsApi.getSubscription(member.id)
      .then((resp) => {
        setSubscriptionData(resp.data);

        setLoading(false);
      })
      .catch((e) => {
        handleServerError(e);
        setLoading(false);
      });
  }, [member.id, member.clover, setSubscriptionData]);

  useEffect(() => {
    if (member.plan === MemberDtoPlanEnum.Free) return;
    setMember(old => {
      return {
        ...old,
        stripePlanType: planType === SubStatusDtoPlanTypeEnum.Pro ? DashboardDtoStripePlanTypeEnum.Pro : (planType === SubStatusDtoPlanTypeEnum.Design ? DashboardDtoStripePlanTypeEnum.Design : DashboardDtoStripePlanTypeEnum.Free),
      }
    });
  }, [planType, member.plan, setMember]);

  useEffect(fetchSubscription, [fetchSubscription]);

  const fetchDeactivatedDevices = useCallback(() => {
    DSAApi.getDeactivatedScreensTotal().then(r => {
      setDeactivatedDevices(r.data);
    }).catch(e => {
      handleServerError(e, "Failed to fetch deactivated devices.");
    });
  }, []);

  useEffect(fetchDeactivatedDevices, [fetchDeactivatedDevices]);

  function onCancelSubscriptionClick() {
    window.MHM.ProModal.cancel({
      memberId: member.id, onSuccess: () => {
        fetchSubscription();
        window.MHM.ProModal.destroy();
      }
    });
  }

  function onPauseSubscriptionClick() {
    window.MHM.ProModal.pause({
      memberId: member.id, onSuccess: () => {
        fetchSubscription();
        window.MHM.ProModal.destroy();
      }
    });
  }

  function onSwitchToAnnualClick() {
    window.MHM.ProModal.switchToAnnual({
      memberId: member.id,
      onSuccess: () => {
        fetchSubscription();
        window.MHM.ProModal.destroy();
      }
    });
  }

  function onSwitchToMonthlyClick() {
    window.MHM.ProModal.switchToMonthly({
      memberId: member.id,
      onSuccess: () => {
        fetchSubscription();
        window.MHM.ProModal.destroy();
      }
    });
  }

  function onUpgradeToProClick() {
    window.MHM.ProModal.switchToProWithOverview({
      memberId: member.id,
      planLength: cycle === 'Monthly' ? 'monthly' : 'annual',
      onSuccess: () => {
        fetchSubscription();
        window.MHM.ProModal.destroy();
      }
    });
  }

  function onDowngradeToDesignClick() {
    setShowLoseAccessWarningModal(true);
  }

  function onLoseAccessWarningModalClose() {
    setShowLoseAccessWarningModal(false);
  }

  function proceedToDowngrade() {
    onLoseAccessWarningModalClose();
    window.MHM.ProModal.switchToDesign({
      memberId: member.id,
      planLength: cycle === 'Monthly' ? 'monthly' : 'annual',
      onSuccess: () => {
        fetchSubscription();
        window.MHM.ProModal.destroy();
      },
      downgrade: true
    });
  }

  function onResumeSubscriptionClick() {
    setPauseLoading(true);
    window.MHM.ProModal.resume({
      memberId: member.id, onSuccess: () => {
        fetchSubscription();
        setPauseLoading(false);
        window.MHM.ProModal.destroy();
      }
    });
  }

  function onReactivateSubscriptionClick() {
    setPauseLoading(true);
    window.MHM.ProModal.reactivate({
      memberId: member.id, onSuccess: () => {
        fetchSubscription();
        setPauseLoading(false);
        window.MHM.ProModal.destroy();
      }
    });
  }

  const getCharge = useCallback((addons: AddOnDto[], cycle: SubStatusDtoPlanCycleEnum, addonType: AddOnDtoTypeEnum, deactivatedCount: number) => {
    if (!addons.length || !addons.filter(it => it.type === addonType).length) return NO_ADDON;
    return <Price amount={getAddonsRecurringCharge(addons, addonType, deactivatedDevices)} cycle={cycle} />;
  }, [deactivatedDevices]);

  const locationCount = useMemo(() => getCount(addons, AddOnDtoTypeEnum.Location, 'Location', 0), [addons]);
  const licenseCount = useMemo(() => getCount(addons, AddOnDtoTypeEnum.Dsa, 'License', deactivatedDevices), [addons, deactivatedDevices]);
  const playerCount = useMemo(() => getCount(addons, AddOnDtoTypeEnum.Dsa, 'Player', deactivatedDevices), [addons, deactivatedDevices]);

  const locationCharge = useMemo(() => getCharge(addons, cycle, AddOnDtoTypeEnum.Location, 0), [addons, cycle, getCharge]);
  const licenseCharge = useMemo(() => getCharge(addons, cycle, AddOnDtoTypeEnum.Dsa, deactivatedDevices), [addons, cycle, getCharge, deactivatedDevices]);

  useEffect(() => {
    if (!member.id) return;
    AccountsApi.getUsersDetails(member.id, 'active', 1, 2, '').then((resp) => {
      setUserCount(resp.data.users?.length || 0);
    });
  }, [member.id]);

  useEffect(() => {
    setDowngradeToDesignButton(planType === SubStatusDtoPlanTypeEnum.Pro);
  }, [planType]);

  useEffect(() => {
    setDowngradeToDesignButtonDisabled(!!licenseCount || !!locationCount || !!userCount);
  }, [licenseCount, locationCount, userCount]);

  useEffect(() => {
    setSwitchToAnnualButton(
      cycle === SubStatusDtoPlanCycleEnum.Monthly &&
      status === SubStatusDtoStatusEnum.Active &&
      !pendingUpgrade
    );
    setSwitchToMonthlyButton(
      cycle === SubStatusDtoPlanCycleEnum.Yearly &&
      status === SubStatusDtoStatusEnum.Active &&
      !pendingUpgrade
    );
  }, [cycle, status, pendingUpgrade]);

  function getCount(addons: AddOnDto[], addonType: AddOnDtoTypeEnum, label: string, deactivatedCount: number) {
    if (!addons.length) return NO_ADDON;
    let count = addons.find((it) => it.type === addonType)?.quantity;
    if (!count) return NO_ADDON;
    count -= deactivatedCount;
    return `${count} ${label}${count > 1 ? "s" : ""}`;
  }

  const planRecurringChargeLabel = useMemo(() => {
    if (cycle === SubStatusDtoPlanCycleEnum.No) {
      return "-";
    } else {
      return <Price amount={charge} cycle={cycle} />;
    }
  }, [cycle, charge]);

  const statusLabel = useMemo(() => {
    if (member.plan === MemberDtoPlanEnum.Free) {
      return "No subscription";
    }

    if (
      status !== SubStatusDtoStatusEnum.WillPause &&
      status !== SubStatusDtoStatusEnum.Inactive
    ) {
      return status;
    }

    if (pauseStartAt || nextPlanBillingAt) {
      if (status === SubStatusDtoStatusEnum.WillPause) {
        return (
          <>
            Paused, effective{" "}
            <DateTimeStamp date={pauseStartAt || nextPlanBillingAt} shortDateOnly />
          </>
        );
      }

      if (status === SubStatusDtoStatusEnum.Inactive) {
        if (addons.length && addonsEndAt) {
          return (
            <>
              Canceled, effective{" "}
              <DateTimeStamp date={addonsEndAt} shortDateOnly />
            </>
          );
        } else {
          return (
            <>
              Canceled, effective{" "}
              <DateTimeStamp date={nextPlanBillingAt} shortDateOnly />
            </>
          );
        }
      }

      return (
        <>
          &nbsp;- Next billing on{" "}
          <DateTimeStamp date={pauseEndAt || nextPlanBillingAt} light />
        </>
      );
    }
  }, [member.plan, nextPlanBillingAt, pauseEndAt, pauseStartAt, status, addons, addonsEndAt]);

  const nextPlanBillingDateLabel = useMemo(() => {
    if (!planEndsAt || status === SubStatusDtoStatusEnum.Inactive) {
      return "-";
    } else {
      return <DateTimeStamp date={planEndsAt} dateOnly />;
    }
  }, [planEndsAt, status]);

  function onManageLocationsClick() {
    navigate(env.dashboardPath + "/account/locations");
  }

  function onOrderClick() {
    window.MHM.ProModal.addDSA({ memberId: `${member.ownerId}` || member.id, onSuccess: () => window.location.reload() });
  }

  const planName = useMemo(() => {
    if (planType === SubStatusDtoPlanTypeEnum.Pro) {
      return <><ProStarFilledIcon /> Pro Plan {cycle === SubStatusDtoPlanCycleEnum.Monthly ? 'Monthly' : 'Annual'}</>;
    } else {
      return <><DesignPlanIcon /> Design Plan {cycle === SubStatusDtoPlanCycleEnum.Monthly ? 'Monthly' : 'Annual'}</>;
    }
  }, [cycle, planType]);

  return (
    <div className={"content subscription-page"}>
      {loading && <Skeleton />}

      {!loading && member.plan === MemberDtoPlanEnum.Free && (
        <SubscriptionFreePageV2 />
      )}

      {!loading && member.plan !== MemberDtoPlanEnum.Free &&
        <>
          {member.clover && <section className={'clover'}>
            <div><CloverLogo /></div>
            <div>You are subscribed through the Clover App Marketplace.<br />Please manage your subscription from there.</div>
          </section>}
          {!member.clover && <>
            <h3>Subscription</h3>

            <div className={'items'}>
              <section className={'subscription-item pro'}>
                <div className={'info'}>
                  <h2>{planName}</h2>
                  <div className={'container'}>
                    <div className={'left'}>
                      <div><span>Status</span><span>{statusLabel}</span></div>
                      <div><span>Plan Recurring Charge</span><span>{planRecurringChargeLabel}</span></div>
                      {isShamrock
                        ? <div><span>Due</span><span><Price amount={charge} /></span></div>
                        : <div><span>Next Plan Billing Date</span><span>{nextPlanBillingDateLabel}</span></div>}
                    </div>
                  </div>
                </div>
                {member.owner && <div className={'right actions'}>
                  {upgradeToProButton && <Button className={"primary"} onClick={onUpgradeToProClick}>Upgrade to Pro Plan</Button>}
                  {downgradeToDesignButton && <Button disabled={downgradeToDesignButtonDisabled} className={"secondary"} onClick={onDowngradeToDesignClick}>Downgrade to Design Plan</Button>}
                  {switchToAnnualButton && <Button className={"secondary"} onClick={onSwitchToAnnualClick}>Switch to Annual</Button>}
                  {switchToMonthlyButton && <Button className={"secondary"} onClick={onSwitchToMonthlyClick}>Switch to Monthly</Button>}
                  {pauseButton && <Button className={"secondary"} onClick={onPauseSubscriptionClick}>Pause Subscription</Button>}
                  {stopPauseButton && <Button className={"secondary"} onClick={onResumeSubscriptionClick} loading={pauseLoading}>Stop Pause</Button>}
                  {stopCancelButton && <Button className={"secondary"} onClick={onReactivateSubscriptionClick} loading={pauseLoading}>Stop Cancel</Button>}
                  {cancelButton && <Button className={'cancel'} type={"text"} onClick={onCancelSubscriptionClick}>Cancel Subscription</Button>}
                </div>}
              </section>

              {!!licenseCount && <section className={'subscription-item display'}>
                <div className={'info'}>
                  <h2><img src="https://timber.mhmcdn.com/site/marketing/icons/DSARed.svg" alt="display" /> Display</h2>
                  <div className={'container'}>
                    <div className={'left'}>
                      <div><span>Skykit Players</span><span>{playerCount}</span></div>
                      <div><span>Licenses</span><span>{licenseCount}</span></div>
                      <div><span>License Recurring Charge</span><span>{licenseCharge}</span></div>
                    </div>
                  </div>
                </div>

                <div className={'right actions'}><Button className={"primary"} onClick={onOrderClick}>Order Devices & Licenses</Button></div>
              </section>}

              {!!locationCount && <section className={'subscription-item location'}>
                <div className={'info'}>
                  <h2><LocationIconRoundCircle /> Additional Locations</h2>
                  <div className={'container'}>
                    <div className={'left'}>
                      <div><span>Add-ons</span><span>{locationCount}</span></div>
                      <div><span>Add-on Recurring Charge</span><span>{locationCharge}</span></div>
                      {isShamrock
                        ? <div><span>Due</span><span><Price amount={getAddonsRecurringCharge(addons, AddOnDtoTypeEnum.Location, 0)} /></span></div>
                        : <div></div>}
                    </div>
                  </div>
                </div>

                <div className={'right actions'}><Button className={"secondary-two"} onClick={onManageLocationsClick}>Manage Locations</Button></div>
              </section>}
            </div>

            <LoseAccessWarningModal visible={showLoseAccessWarningModal} onClose={onLoseAccessWarningModalClose} onOk={proceedToDowngrade}/>
          </>}
        </>
      }
    </div >
  );
}
